import React, { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { formatCurrency } from '../lib/utils';
import { 
  Target, 
  Plus, 
  Minus,
  ChevronDown,
  ChevronUp,
  AlertTriangle
} from 'lucide-react';

export function Budget() {
  const { 
    state, 
    storage,
    getCategories, 
    getBudgetAllocations, 
    updateBudgetAllocation 
  } = useApp();
  
  const categories = getCategories().filter(c => c.envelope);
  const budgetAllocations = getBudgetAllocations();
  const transactions = storage.getTransactions();
  const schedules = storage.getSchedules();
  
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['Housing', 'Food', 'Transportation']));
  const [monthlyIncomeTarget, setMonthlyIncomeTarget] = useState<number>(3000);
  const [isEditingIncome, setIsEditingIncome] = useState(false);

  // Group categories
  const groupedCategories = categories.reduce((groups, category) => {
    const group = category.group || 'Other';
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(category);
    return groups;
  }, {} as Record<string, typeof categories>);

  const toggleGroup = (group: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(group)) {
      newExpanded.delete(group);
    } else {
      newExpanded.add(group);
    }
    setExpandedGroups(newExpanded);
  };

  const handleAllocationChange = (categoryId: string, amount: string) => {
    const numAmount = parseFloat(amount) || 0;
    updateBudgetAllocation(categoryId, state.currentMonth, numAmount);
  };

  const getAllocationData = (categoryId: string) => {
    return budgetAllocations.find(a => 
      a.categoryId === categoryId && a.month === state.currentMonth
    ) || { allocated: 0, spent: 0, rollover: 0 };
  };

  const totalAllocated = budgetAllocations
    .filter(a => a.month === state.currentMonth)
    .reduce((sum, a) => sum + a.allocated, 0);

  const totalSpent = budgetAllocations
    .filter(a => a.month === state.currentMonth)
    .reduce((sum, a) => sum + Math.abs(a.spent), 0);

  // Calculate actual income for the current month
  const calculateMonthlyIncome = () => {
    const [year, month] = state.currentMonth.split('-').map(Number);
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0);
    
    // Get actual income transactions for this month
    const monthTransactions = transactions.filter(t => {
      const tDate = new Date(t.date);
      return t.amount > 0 && tDate >= monthStart && tDate <= monthEnd;
    });
    
    const actualIncome = monthTransactions.reduce((sum, t) => sum + t.amount, 0);
    
    // Add scheduled income for this month (if not already posted)
    const incomeSchedules = schedules.filter(s => s.active && s.type === 'income');
    let scheduledIncome = 0;
    
    incomeSchedules.forEach(schedule => {
      // Simple calculation - in a real app, would use the scheduling engine
      const monthlyAmount = schedule.template.amount;
      if (schedule.spec.includes('MONTHLY')) {
        scheduledIncome += monthlyAmount;
      } else if (schedule.spec.includes('WEEKLY') && schedule.spec.includes('INTERVAL=2')) {
        // Bi-weekly: ~2.17 times per month
        scheduledIncome += monthlyAmount * 2.17;
      } else if (schedule.spec.includes('WEEKLY')) {
        // Weekly: ~4.33 times per month
        scheduledIncome += monthlyAmount * 4.33;
      }
    });
    
    // Return the higher of actual income or target income or scheduled income
    return Math.max(actualIncome, monthlyIncomeTarget, scheduledIncome);
  };

  const monthlyIncome = calculateMonthlyIncome();
  const toBeAllocated = monthlyIncome - totalAllocated;

  const handleIncomeTargetChange = (value: string) => {
    const numValue = parseFloat(value) || 0;
    setMonthlyIncomeTarget(numValue);
    // In a real app, this would be persisted to storage
    localStorage.setItem('monthlyIncomeTarget', numValue.toString());
  };

  // Load income target from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('monthlyIncomeTarget');
    if (saved) {
      setMonthlyIncomeTarget(parseFloat(saved));
    }
  }, []);

  return (
    <div className="py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Budget</h1>
          <p className="text-muted-foreground">
            {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Budget Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Monthly Income - Editable */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              Monthly Income
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditingIncome(!isEditingIncome)}
                className="h-6 w-6 p-0"
              >
                {isEditingIncome ? '✓' : '✏️'}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isEditingIncome ? (
              <Input
                type="number"
                step="0.01"
                value={monthlyIncomeTarget}
                onChange={(e) => handleIncomeTargetChange(e.target.value)}
                onBlur={() => setIsEditingIncome(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setIsEditingIncome(false);
                  }
                }}
                className="text-lg font-bold"
                autoFocus
              />
            ) : (
              <div className="text-2xl font-bold text-blue-600 cursor-pointer" 
                   onClick={() => setIsEditingIncome(true)}>
                {formatCurrency(monthlyIncome)}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {monthlyIncome > monthlyIncomeTarget ? 'Includes actual/scheduled' : 'Target amount'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">To Be Allocated</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${toBeAllocated >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {formatCurrency(toBeAllocated)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {toBeAllocated >= 0 
                ? `${formatCurrency(Math.abs(toBeAllocated))} available to budget`
                : `${formatCurrency(Math.abs(toBeAllocated))} over-allocated`
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Allocated</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {formatCurrency(totalAllocated)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {((totalAllocated / monthlyIncome) * 100).toFixed(1)}% of income
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Spent</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {formatCurrency(totalSpent)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {totalAllocated > 0 ? `${((totalSpent / totalAllocated) * 100).toFixed(1)}% of allocated` : '0% of allocated'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Remaining</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${(totalAllocated - totalSpent) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {formatCurrency(totalAllocated - totalSpent)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              In allocated budgets
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      {toBeAllocated > 0 && (
        <Card className="border-dashed border-2 border-emerald-200 bg-emerald-50 dark:bg-emerald-950 dark:border-emerald-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-emerald-800 dark:text-emerald-200">
                  You have {formatCurrency(toBeAllocated)} to allocate
                </h3>
                <p className="text-sm text-emerald-600 dark:text-emerald-400">
                  Quick actions to allocate your remaining funds
                </p>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Allocate remaining funds to Emergency Fund category
                    const emergencyCategory = categories.find(c => 
                      c.name.toLowerCase().includes('emergency') || c.name.toLowerCase().includes('fund')
                    );
                    if (emergencyCategory) {
                      const currentAllocation = getAllocationData(emergencyCategory.id);
                      handleAllocationChange(emergencyCategory.id, (currentAllocation.allocated + toBeAllocated).toString());
                    }
                  }}
                  className="text-emerald-700 border-emerald-300"
                >
                  → Emergency Fund
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Distribute evenly among categories with targets
                    const categoriesWithTargets = categories.filter(c => c.target && c.target > 0);
                    if (categoriesWithTargets.length > 0) {
                      const perCategory = Math.floor(toBeAllocated / categoriesWithTargets.length);
                      categoriesWithTargets.forEach(category => {
                        const currentAllocation = getAllocationData(category.id);
                        handleAllocationChange(category.id, (currentAllocation.allocated + perCategory).toString());
                      });
                    }
                  }}
                  className="text-emerald-700 border-emerald-300"
                >
                  Distribute Evenly
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Budget Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Target className="w-5 h-5 mr-2" />
              Category Allocations
            </div>
            <div className="text-sm text-muted-foreground">
              {categories.length} envelope categories • {Object.keys(groupedCategories).length} groups
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(groupedCategories).map(([groupName, groupCategories]) => (
            <div key={groupName} className="border rounded-lg">
              <button
                onClick={() => toggleGroup(groupName)}
                className="w-full flex items-center justify-between p-3 text-left hover:bg-muted/50"
              >
                <span className="font-semibold">{groupName}</span>
                {expandedGroups.has(groupName) ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              
              {expandedGroups.has(groupName) && (
                <div className="border-t p-3 space-y-4">
                  {groupCategories.map((category) => {
                    const allocation = getAllocationData(category.id);
                    const available = allocation.allocated + allocation.rollover - Math.abs(allocation.spent);
                    const progressPercent = allocation.allocated > 0 
                      ? Math.min((Math.abs(allocation.spent) / allocation.allocated) * 100, 100)
                      : 0;

                    return (
                      <div key={category.id} className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium">{category.name}</h4>
                              <div className="text-sm text-muted-foreground">
                                {allocation.allocated > 0 && (
                                  <span>{((allocation.allocated / monthlyIncome) * 100).toFixed(1)}% of income</span>
                                )}
                              </div>
                            </div>
                            {category.target && (
                              <p className="text-xs text-muted-foreground">
                                Target: {formatCurrency(category.target)}
                                {allocation.allocated >= category.target && (
                                  <span className="text-emerald-600 ml-2">✓ Target met</span>
                                )}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAllocationChange(
                              category.id, 
                              Math.max(0, allocation.allocated - 25).toString()
                            )}
                            disabled={allocation.allocated <= 0}
                          >
                            <Minus className="w-3 h-3" />
                          </Button>
                          
                          <div className="flex-1 flex items-center space-x-2">
                            <span className="text-sm text-muted-foreground w-8">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={allocation.allocated || ''}
                              onChange={(e) => handleAllocationChange(category.id, e.target.value)}
                              className="text-center font-medium"
                              placeholder="0.00"
                            />
                          </div>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAllocationChange(
                              category.id, 
                              (allocation.allocated + 25).toString()
                            )}
                          >
                            <Plus className="w-3 h-3" />
                          </Button>
                          
                          {category.target && category.target > allocation.allocated && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAllocationChange(category.id, category.target!.toString())}
                              className="text-xs text-emerald-600 hover:text-emerald-700 px-2"
                            >
                              Target
                            </Button>
                          )}
                        </div>

                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Spent: {formatCurrency(Math.abs(allocation.spent))}</span>
                            <div className="flex items-center space-x-2">
                              <span className={available >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                                {available >= 0 ? 'Remaining: ' : 'Overspent: '}
                                {formatCurrency(Math.abs(available))}
                              </span>
                              {allocation.allocated > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleAllocationChange(category.id, '0')}
                                  className="text-xs text-muted-foreground hover:text-red-600 px-2 h-6"
                                >
                                  Clear
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          <div className="space-y-1">
                            <Progress 
                              value={progressPercent} 
                              className={`h-3 ${progressPercent > 100 ? 'bg-red-100' : ''}`}
                            />
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <span>{progressPercent.toFixed(1)}% used</span>
                              {allocation.rollover !== 0 && (
                                <span className={allocation.rollover > 0 ? 'text-blue-600' : 'text-orange-600'}>
                                  Rollover: {formatCurrency(allocation.rollover)}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {available < 0 && (
                            <div className="flex items-center text-sm text-red-600 bg-red-50 dark:bg-red-950 px-2 py-1 rounded">
                              <AlertTriangle className="w-4 h-4 mr-1" />
                              This category is over budget by {formatCurrency(Math.abs(available))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}